import React from 'react'
import {
  getIntelligentAskAmount,
  IntelligentAskAmountResponse,
} from 'services/intelligentAskAmount/intelligent-ask-amount'
import { EVENT } from 'models/event'
import { PageConfig } from 'models/pages'

export interface IntelligentAskAmountEventPayload extends IntelligentAskAmountResponse {
  isLoading: boolean
  onetimeEnabled: boolean
  recurringEnabled: boolean
}

/**
 * Custom React hook that manages fetching and dispatching Intelligent Ask Amounts.
 */
export const useIntelligentAskAmount = (
  campaignId: PageConfig['campaignId'],
  config: PageConfig['intelligentAskConfig'],
) => {
  const onetimeEnabled = Boolean(config?.onetimeEnabled)
  const recurringEnabled = Boolean(config?.recurringEnabled)

  const intelligentAskAmounts = React.useCallback(async () => {
    if (!campaignId || (!onetimeEnabled && !recurringEnabled)) {
      return
    }

    const iaaBaseAmounts = await getIntelligentAskAmount(campaignId, config?.isPreviewPage)

    document.dispatchEvent(
      new CustomEvent(EVENT.IAA_FETCH_AMOUNTS, {
        detail: {
          ...iaaBaseAmounts,
          isLoading: false,
          onetimeEnabled,
          recurringEnabled,
        },
      }),
    )
  }, [campaignId, config?.isPreviewPage, onetimeEnabled, recurringEnabled])

  React.useEffect(() => {
    if (onetimeEnabled || recurringEnabled) {
      intelligentAskAmounts()
    } else {
      /**
       * Donation block is initially set to think IAA values are being fetched.
       * If IAA isn't being used, immediately fire an event with { isLoading: true } to stop
       * the loading animation.
       */
      document.dispatchEvent(
        new CustomEvent(EVENT.IAA_FETCH_AMOUNTS, {
          detail: {
            isLoading: false,
            onetimeEnabled,
            recurringEnabled,
          },
        }),
      )
    }
  }, [intelligentAskAmounts, onetimeEnabled, recurringEnabled])
}
